import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui-kit/dropdown-menu';
import { useSignoutMutation } from '@/modules/auth/hooks/use-auth';
import { useAuthStore } from '@/state/store/auth';
import DummyProfile from '@/assets/images/dummy_profile.png';
import { Skeleton } from '@/components/ui-kit/skeleton';
import { useGetAccount } from '@/modules/profile/hooks/use-account';
import { cn } from '@/lib/utils';
import { OrganizationMenuSection } from './organization-menu-section';

const getInitialsFromName = (name: string): string => {
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  return '?';
};

/**
 * ProfileMenu Component
 *
 * A user profile dropdown menu component that displays user information and provides
 * navigation and account management options.
 *
 * Features:
 * - Displays user profile image when available, otherwise initials avatar
 * - Shows loading states with skeleton placeholders
 * - Provides navigation to profile page
 * - Handles user logout with authentication state management
 *
 * Dependencies:
 * - Requires useAuthStore for authentication state management
 * - Requires useSignoutMutation for API logout functionality
 * - Requires useGetAccount for fetching user account data
 * - Uses DropdownMenu components for the menu interface
 * - Uses React Router's useNavigate for navigation
 *
 * @example
 * // Basic usage in a header or navigation component
 * <ProfileMenu />
 */

type ProfileMenuProps = {
  variant?: 'default' | 'compact';
};

export const ProfileMenu = ({ variant = 'default' }: ProfileMenuProps) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);
  const { t } = useTranslation();

  const { logout } = useAuthStore();
  const { mutateAsync } = useSignoutMutation();
  const navigate = useNavigate();
  const { data, isLoading } = useGetAccount();

  const signoutHandler = async () => {
    try {
      const res = await mutateAsync();
      if (res.isSuccess) {
        logout();
        navigate('/login');
      }
    } catch (_error) {
      /* empty */
    }
  };

  const fullName = `${data?.firstName ?? ''} ${data?.lastName ?? ''}`.trim();
  const initials = fullName
    ? getInitialsFromName(fullName)
    : (data?.email?.slice(0, 2).toUpperCase() ?? '?');
  const hasProfileImage = Boolean(data?.profileImageUrl?.trim());
  const profileImageUrl = data?.profileImageUrl ?? '';

  useEffect(() => {
    if (data) {
      localStorage.setItem(
        'userProfile',
        JSON.stringify({
          fullName,
          profileImageUrl: data.profileImageUrl || DummyProfile,
        })
      );
    }
  }, [data, fullName]);

  useEffect(() => {
    setIsImageLoaded(false);
    setImageError(false);
  }, [profileImageUrl]);

  useEffect(() => {
    const img = imgRef.current;
    if (img && img.complete && img.naturalHeight !== 0) {
      setIsImageLoaded(true);
    }
  }, [profileImageUrl, isLoading]);

  const showImage = hasProfileImage && !imageError;
  const isCompact = variant === 'compact';

  return (
    <DropdownMenu open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
      <DropdownMenuTrigger asChild className="cursor-pointer rounded-[2px] p-0">
        <div className="flex cursor-pointer items-center justify-between gap-1 sm:gap-3">
          {isLoading ? (
            <Skeleton className="h-8 w-8 rounded-full" />
          ) : showImage ? (
            <div className="relative h-8 w-8 shrink-0 overflow-hidden rounded-full">
              {!isImageLoaded && (
                <Skeleton className="absolute inset-0 z-10 h-8 w-8 rounded-full" />
              )}
              <img
                ref={imgRef}
                src={profileImageUrl}
                alt="profile"
                className="h-full w-full object-cover"
                style={{ opacity: isImageLoaded ? 1 : 0 }}
                onLoad={() => setIsImageLoaded(true)}
                onError={() => setImageError(true)}
              />
            </div>
          ) : (
            <div
              className={cn(
                'flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold',
                isCompact
                  ? 'bg-primary-600 text-white'
                  : 'bg-[#0161AB]/10 text-[#0161AB]'
              )}
            >
              {initials}
            </div>
          )}
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="w-72 text-medium-emphasis"
        align="end"
        sideOffset={10}
      >
        <OrganizationMenuSection onOrganizationSelected={() => setIsDropdownOpen(false)} />
        <DropdownMenuItem onClick={() => navigate('/profile')}>{t('MY_PROFILE')}</DropdownMenuItem>
        <DropdownMenuItem disabled>{t('ABOUT')}</DropdownMenuItem>
        <DropdownMenuItem disabled>{t('PRIVACY_POLICY')}</DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={signoutHandler}>{t('LOG_OUT')}</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
